import { Home, Plus, Trash2 } from 'lucide-react';
import { Section, Field, Toggle, EditButton, EditActions } from '../ui';
import { ic } from '../helpers';
import {
  PROPERTY_TYPES, ROOF_MATERIALS, ROOF_SHAPES,
  CONSTRUCTION_TYPES, BASEMENT_TYPES,
} from '../constants';

// ─────────────────────────────────────────
// PROPERTIES SECTION
// One card per property with read/edit toggle, plus inline prior-losses editor.
// All state lives in the parent — this component just renders + emits events.
// ─────────────────────────────────────────
export default function PropertiesSection({
  properties,
  propertyLosses,
  editingPropertyId,
  propertyDraft,
  setPropertyDraft,
  saving,
  onAddProperty,
  onStartEditProperty,
  onSaveProperty,
  onCancelEditProperty,
  onDeleteProperty,
  onAddLoss,
  onUpdateLossField,
  onSaveLoss,
  onDeleteLoss,
}) {
  return (
    <Section
      title={`Properties (${properties.length})`}
      icon={Home}
      action={
        <button
          onClick={onAddProperty}
          className="flex items-center gap-1 px-3 py-1 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition"
        >
          <Plus size={11} /> Add Property
        </button>
      }
    >
      <div className="space-y-4 pt-3">
        {properties.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-4">No properties added yet.</p>
        )}

        {properties.map((prop) => {
          const isEditing = editingPropertyId === prop.id;
          const draft = isEditing ? propertyDraft : prop;
          const update = (key, val) => setPropertyDraft(p => ({ ...p, [key]: val }));
          const losses = propertyLosses[prop.id] || [];

          return (
            <div key={prop.id} className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b">
                <div className="flex items-center gap-2">
                  <Home size={14} className="text-blue-600" />
                  <span className="text-sm font-semibold text-slate-700">{prop.propertyType}</span>
                  {prop.address && <span className="text-xs text-slate-400">— {prop.address}</span>}
                </div>
                <div className="flex items-center gap-2">
                  {isEditing
                    ? <EditActions onSave={() => onSaveProperty(prop.id)} onCancel={onCancelEditProperty} saving={saving} />
                    : <>
                      <EditButton onClick={() => onStartEditProperty(prop)} />
                      <button onClick={() => onDeleteProperty(prop.id)} className="p-1 text-slate-400 hover:text-red-500 transition">
                        <Trash2 size={13} />
                      </button>
                    </>
                  }
                </div>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3">
                {isEditing ? (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Property Type</label>
                      <select value={draft.propertyType} onChange={e => update('propertyType', e.target.value)} className={ic}>
                        {PROPERTY_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Address</label>
                      <input value={draft.address || ''} onChange={e => update('address', e.target.value)} className={ic} />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-slate-500 mb-1">Title Holder</label>
                      <input value={draft.titleHolder || ''} onChange={e => update('titleHolder', e.target.value)} className={ic} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Roof Year</label>
                      <input type="number" value={draft.roofYear || ''} onChange={e => update('roofYear', e.target.value)} className={ic} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Roof Material</label>
                      <select value={draft.roofMaterial || ''} onChange={e => update('roofMaterial', e.target.value)} className={ic}>
                        <option value="">Select...</option>
                        {ROOF_MATERIALS.map(m => <option key={m}>{m}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2 space-y-1 bg-slate-50 rounded-xl px-4 py-2 divide-y divide-slate-200">
                      {[
                        { key: 'hasMortgage', label: 'Has Mortgage' },
                        { key: 'escrowPremium', label: 'Premium via Escrow' },
                        { key: 'roofClassIV', label: 'Class IV Hail-Resistant Roof' },
                        { key: 'centralFireAlarm', label: 'Central Fire Alarm' },
                        { key: 'centralBurglarAlarm', label: 'Central Burglar Alarm' },
                        { key: 'lowTempSensor', label: 'Low-Temp Sensor' },
                        { key: 'sprinklerSystem', label: 'Sprinkler System' },
                        { key: 'permanentGenerator', label: 'Permanent Generator' },
                        { key: 'gatedCommunity', label: 'Gated Community' },
                        { key: 'autoWaterShutoff', label: 'Auto Water Shutoff' },
                      ].map(({ key, label }) => (
                        <Toggle key={key} label={label} checked={!!draft[key]} onChange={v => update(key, v)} editing={true} />
                      ))}
                    </div>

                    {/* ── Residence Usage ── */}
                    <div className="col-span-2 pt-1 border-t border-slate-100">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Residence Usage</p>
                    </div>
                    <div className="col-span-2 space-y-1 bg-slate-50 rounded-xl px-4 py-2 divide-y divide-slate-200">
                      {[
                        { key: 'isVacant', label: 'Is the residence vacant?' },
                        { key: 'isUnderConstruction', label: 'Is the residence under construction or renovation?' },
                        { key: 'isForSale', label: 'Is the residence currently for sale?' },
                        { key: 'isHistoricRegistry', label: 'Is this location listed on national, state, or local register of historic places?' },
                      ].map(({ key, label }) => (
                        <Toggle key={key} label={label} checked={!!draft[key]} onChange={v => update(key, v)} editing={true} />
                      ))}
                    </div>

                    <div><label className="block text-xs font-medium text-slate-500 mb-1">Water Heater Year</label><input type="number" value={draft.waterHeaterYear || ''} onChange={e => update('waterHeaterYear', e.target.value)} className={ic} /></div>
                    <div><label className="block text-xs font-medium text-slate-500 mb-1">HVAC Year</label><input type="number" value={draft.hvacYear || ''} onChange={e => update('hvacYear', e.target.value)} className={ic} /></div>
                    <div><label className="block text-xs font-medium text-slate-500 mb-1">Plumbing Year</label><input type="number" value={draft.plumbingYear || ''} onChange={e => update('plumbingYear', e.target.value)} className={ic} /></div>
                    <div><label className="block text-xs font-medium text-slate-500 mb-1">Electrical Year</label><input type="number" value={draft.electricalYear || ''} onChange={e => update('electricalYear', e.target.value)} className={ic} /></div>

                    <div className="col-span-2 pt-1 border-t border-slate-100">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Home Details</p>
                    </div>
                    <div><label className="block text-xs font-medium text-slate-500 mb-1">Year Built</label><input type="number" value={draft.yearBuilt || ''} onChange={e => update('yearBuilt', e.target.value)} className={ic} /></div>
                    <div><label className="block text-xs font-medium text-slate-500 mb-1">Sq. Footage</label><input type="number" value={draft.squareFootage || ''} onChange={e => update('squareFootage', e.target.value)} className={ic} /></div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Construction Type</label>
                      <select value={draft.constructionType || ''} onChange={e => update('constructionType', e.target.value)} className={ic}>
                        <option value="">—</option>
                        {CONSTRUCTION_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div><label className="block text-xs font-medium text-slate-500 mb-1">Num. Floors</label><input type="number" value={draft.numFloors || ''} onChange={e => update('numFloors', e.target.value)} className={ic} /></div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Roof Shape</label>
                      <select value={draft.roofShape || ''} onChange={e => update('roofShape', e.target.value)} className={ic}>
                        <option value="">—</option>
                        {ROOF_SHAPES.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Swimming Pool</label>
                      <select
                        value={draft.hasPool === true ? 'yes' : draft.hasPool === false ? 'no' : ''}
                        onChange={e => update('hasPool', e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null)}
                        className={ic}
                      >
                        <option value="">Unknown</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>

                    <div className="col-span-2 space-y-1 bg-slate-50 rounded-xl px-4 py-2 divide-y divide-slate-200">
                      <Toggle label="Has Basement" checked={!!draft.hasBasement} onChange={v => update('hasBasement', v)} editing={true} />
                    </div>
                    {draft.hasBasement && (<>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Basement Type</label>
                        <select value={draft.basementType || ''} onChange={e => update('basementType', e.target.value)} className={ic}>
                          <option value="">—</option>
                          {BASEMENT_TYPES.map(t => <option key={t}>{t}</option>)}
                        </select>
                      </div>
                      <div><label className="block text-xs font-medium text-slate-500 mb-1">Basement Sq Ft</label><input type="number" value={draft.basementSqFt || ''} onChange={e => update('basementSqFt', e.target.value)} className={ic} /></div>
                    </>)}

                    {draft.hasMortgage && (<>
                      <div className="col-span-2 pt-1 border-t border-slate-100">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Mortgage Lender</p>
                      </div>
                      <div><label className="block text-xs font-medium text-slate-500 mb-1">Lender Name</label><input value={draft.mortgageeName || ''} onChange={e => update('mortgageeName', e.target.value)} className={ic} /></div>
                      <div><label className="block text-xs font-medium text-slate-500 mb-1">Lender Address</label><input value={draft.mortgageeAddress || ''} onChange={e => update('mortgageeAddress', e.target.value)} className={ic} /></div>
                    </>)}

                    <div className="col-span-2 pt-1 border-t border-slate-100">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Coverage Amounts</p>
                    </div>
                    <div><label className="block text-xs font-medium text-slate-500 mb-1">Dwelling ($)</label><input type="number" value={draft.dwellingAmount || ''} onChange={e => update('dwellingAmount', e.target.value)} className={ic} /></div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1 flex items-center gap-1">
                        Replacement Cost (AI) ($)
                        {draft.replacementCostReasoning && (
                          <span
                            className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold cursor-help"
                            title={draft.replacementCostReasoning}
                          >i</span>
                        )}
                      </label>
                      <input
                        type="number"
                        value={draft.estimatedReplacementCost || ''}
                        onChange={e => update('estimatedReplacementCost', e.target.value)}
                        className={ic}
                        placeholder="AI-estimated rebuild cost"
                      />
                    </div>
                    <div><label className="block text-xs font-medium text-slate-500 mb-1">Other Structures ($)</label><input type="number" value={draft.otherStructuresAmount || ''} onChange={e => update('otherStructuresAmount', e.target.value)} className={ic} /></div>
                    <div><label className="block text-xs font-medium text-slate-500 mb-1">Contents ($)</label><input type="number" value={draft.contentsAmount || ''} onChange={e => update('contentsAmount', e.target.value)} className={ic} /></div>
                    <div><label className="block text-xs font-medium text-slate-500 mb-1">Loss of Use</label><input value={draft.lossOfUse || ''} onChange={e => update('lossOfUse', e.target.value)} className={ic} /></div>
                    <div><label className="block text-xs font-medium text-slate-500 mb-1">AOP Deductible</label><input value={draft.aopDeductible || ''} onChange={e => update('aopDeductible', e.target.value)} className={ic} /></div>
                    <div><label className="block text-xs font-medium text-slate-500 mb-1">Liability Limit ($)</label><input type="number" value={draft.liabilityLimit || ''} onChange={e => update('liabilityLimit', e.target.value)} className={ic} /></div>
                    <div><label className="block text-xs font-medium text-slate-500 mb-1">Medical Payments ($)</label><input type="number" value={draft.medicalPayments || ''} onChange={e => update('medicalPayments', e.target.value)} className={ic} /></div>

                    {/* Prior Losses */}
                    <div className="col-span-2 pt-1 border-t border-slate-100">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Prior Losses (past 5 years)</p>
                        <button onClick={() => onAddLoss(prop.id)} className="text-xs text-blue-600 hover:text-blue-700 transition">+ Add loss</button>
                      </div>
                      {losses.length === 0 ? (
                        <p className="text-xs text-slate-400">No prior losses recorded.</p>
                      ) : (
                        <div className="space-y-3">
                          {losses.map(loss => (
                            <div key={loss.id} className="border border-slate-200 rounded-xl p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-slate-600">
                                  {loss.lossDate ? new Date(loss.lossDate).toLocaleDateString() : 'No date'}{loss.lossType ? ` — ${loss.lossType}` : ''}
                                </span>
                                <button onClick={() => onDeleteLoss(prop.id, loss.id)} className="text-xs text-slate-400 hover:text-red-500 transition">Remove</button>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-xs font-medium text-slate-500 mb-1">Date of Loss</label>
                                  <input type="date" value={loss.lossDate ? loss.lossDate.slice(0, 10) : ''}
                                    onChange={e => onUpdateLossField(prop.id, loss.id, 'lossDate', e.target.value)}
                                    onBlur={() => onSaveLoss(prop.id, loss.id)} className={ic} />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-slate-500 mb-1">Loss Type</label>
                                  <input value={loss.lossType || ''}
                                    onChange={e => onUpdateLossField(prop.id, loss.id, 'lossType', e.target.value)}
                                    onBlur={() => onSaveLoss(prop.id, loss.id)} placeholder="e.g. Water, Fire, Wind" className={ic} />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-slate-500 mb-1">Amount Paid ($)</label>
                                  <input type="number" value={loss.amountPaid || ''}
                                    onChange={e => onUpdateLossField(prop.id, loss.id, 'amountPaid', e.target.value)}
                                    onBlur={() => onSaveLoss(prop.id, loss.id)} placeholder="e.g. 15000" className={ic} />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-slate-500 mb-1">Claim Closed?</label>
                                  <select
                                    value={loss.claimClosed == null ? '' : loss.claimClosed ? 'yes' : 'no'}
                                    onChange={e => {
                                      const v = e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null;
                                      onUpdateLossField(prop.id, loss.id, 'claimClosed', v);
                                    }}
                                    onBlur={() => onSaveLoss(prop.id, loss.id)} className={ic}
                                  >
                                    <option value="">Unknown</option>
                                    <option value="yes">Yes</option>
                                    <option value="no">No</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <Field label="Address" value={prop.address} editing={false} />
                    <Field label="Title Holder" value={prop.titleHolder} editing={false} />
                    <Field label="Roof Year" value={prop.roofYear} editing={false} />
                    <Field label="Roof Material" value={prop.roofMaterial} editing={false} />
                    <Field label="Roof Shape" value={prop.roofShape} editing={false} />
                    <Field label="Mortgage" value={prop.hasMortgage ? 'Yes' : 'No'} editing={false} />
                    <Field label="Year Built" value={prop.yearBuilt} editing={false} />
                    <Field label="Sq. Footage" value={prop.squareFootage ? prop.squareFootage.toLocaleString() : null} editing={false} />
                    <Field label="Construction" value={prop.constructionType} editing={false} />
                    <Field label="Floors" value={prop.numFloors} editing={false} />
                    <Field label="Basement" value={prop.hasBasement ? (prop.basementType || 'Yes') : 'No'} editing={false} />
                    {prop.hasPool !== null && prop.hasPool !== undefined && (
                      <Field label="Swimming Pool" value={prop.hasPool ? 'Yes' : 'No'} editing={false} />
                    )}
                    <Field label="Water Heater" value={prop.waterHeaterYear} editing={false} />
                    <Field label="HVAC" value={prop.hvacYear} editing={false} />
                    <Field label="Plumbing" value={prop.plumbingYear} editing={false} />
                    <Field label="Electrical" value={prop.electricalYear} editing={false} />
                    {prop.hasMortgage && <Field label="Lender" value={prop.mortgageeName} editing={false} />}
                    {prop.dwellingAmount && <Field label="Dwelling" value={`$${Number(prop.dwellingAmount).toLocaleString()}`} editing={false} />}
                    {prop.estimatedReplacementCost && (
                      <div>
                        <p className="text-xs font-medium text-slate-400 mb-1 flex items-center gap-1">
                          Replacement Cost (AI)
                          {prop.replacementCostReasoning && (
                            <span
                              className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold cursor-help"
                              title={prop.replacementCostReasoning}
                            >i</span>
                          )}
                        </p>
                        <p className="text-sm text-slate-700">${Number(prop.estimatedReplacementCost).toLocaleString()}</p>
                      </div>
                    )}
                    {prop.liabilityLimit && <Field label="Liability Limit" value={`$${Number(prop.liabilityLimit).toLocaleString()}`} editing={false} />}
                    {prop.aopDeductible && <Field label="AOP Ded." value={prop.aopDeductible} editing={false} />}
                    {prop.isVacant ? <Field label="Vacant" value="Yes" editing={false} /> : null}
                    {prop.isUnderConstruction ? <Field label="Under Construction" value="Yes" editing={false} /> : null}
                    {prop.isForSale ? <Field label="For Sale" value="Yes" editing={false} /> : null}
                    {prop.isHistoricRegistry ? <Field label="Historic Registry" value="Yes" editing={false} /> : null}
                    {losses.length > 0 && (
                      <div className="col-span-2">
                        <p className="text-xs font-medium text-slate-400 mb-1">Prior Losses</p>
                        <div className="space-y-1">
                          {losses.map(l => (
                            <p key={l.id} className="text-sm text-slate-700">
                              {l.lossDate ? new Date(l.lossDate).toLocaleDateString() : '—'}
                              {l.lossType ? ` · ${l.lossType}` : ''}
                              {l.amountPaid ? ` · $${Number(l.amountPaid).toLocaleString()}` : ''}
                              {l.claimClosed != null ? (l.claimClosed ? ' · Closed' : ' · Open') : ''}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}